/**
 * Edit Request Page
 * Allows yacht owners to modify existing mooring assistance requests
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { requestsAPI } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import {
  Calendar,
  Clock,
  Ship,
  Users,
  ArrowLeft,
  Save,
  AlertCircle,
} from 'lucide-react';
import { format } from 'date-fns';

function EditRequest() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useNotifications();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [request, setRequest] = useState(null);

  const [formData, setFormData] = useState({
    scheduled_date: '',
    eta_from: '',
    eta_to: '',
    helpers_needed: 1,
    special_notes: '',
    berth_number: '',
  });

  useEffect(() => {
    loadRequest();
  }, [id]);

  const loadRequest = async () => {
    try {
      const response = await requestsAPI.getById(id);
      const req = response.data || response.request;
      setRequest(req);
      setFormData({
        scheduled_date: req.scheduled_date?.split('T')[0] || '',
        eta_from: req.eta_from || req.scheduled_time || '',
        eta_to: req.eta_to || '',
        helpers_needed: req.helpers_needed || 1,
        special_notes: req.special_notes || req.notes || '',
        berth_number: req.berth_number || '',
      });
    } catch (error) {
      showToast('Failed to load request', 'error');
      navigate('/owner/requests');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await requestsAPI.update(id, formData);
      showToast('Request updated successfully! Applied helpers have been notified.', 'success');
      navigate(`/owner/requests/${id}`);
    } catch (error) {
      showToast(error.message || 'Failed to update request', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  if (!request) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-16 w-16 text-gray-300 mx-auto mb-4" />
        <h2 className="text-xl font-medium text-navy-900">Request not found</h2>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center text-navy-600 hover:text-navy-800 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </button>
        <h1 className="text-2xl font-display font-bold text-navy-900">
          Modify Request
        </h1>
        <p className="text-navy-600 mt-1">
          Update the details of your mooring request. Applied helpers will be notified of changes.
        </p>
      </div>

      {/* Request Info Card */}
      <div className="card mb-6 bg-gray-50">
        <div className="flex items-center">
          <div className="h-12 w-12 bg-white rounded-lg flex items-center justify-center border border-gray-200">
            <Ship className="h-6 w-6 text-navy-400" />
          </div>
          <div className="ml-4">
            <p className="font-medium text-navy-900">{request.yacht_name}</p>
            <p className="text-sm text-navy-500">{request.port_name}</p>
          </div>
        </div>
      </div>

      {/* Warning if helpers have applied */}
      {request.application_count > 0 && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start">
          <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <p className="font-medium text-amber-800">
              {request.application_count} helper{request.application_count > 1 ? 's have' : ' has'} applied
            </p>
            <p className="text-sm text-amber-700 mt-1">
              They will receive a notification and chat message about any changes you make.
            </p>
          </div>
        </div>
      )}

      {/* Edit Form */}
      <form onSubmit={handleSubmit} className="card">
        {/* Date */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-navy-700 mb-2">
            <Calendar className="h-4 w-4 inline mr-1" />
            Date
          </label>
          <input
            type="date"
            name="scheduled_date"
            value={formData.scheduled_date}
            onChange={handleChange}
            min={format(new Date(), 'yyyy-MM-dd')}
            className="input-field"
            required
          />
        </div>

        {/* Time Range */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-navy-700 mb-2">
              <Clock className="h-4 w-4 inline mr-1" />
              ETA From
            </label>
            <input
              type="time"
              name="eta_from"
              value={formData.eta_from}
              onChange={handleChange}
              className="input-field"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-navy-700 mb-2">
              <Clock className="h-4 w-4 inline mr-1" />
              ETA To
            </label>
            <input
              type="time"
              name="eta_to"
              value={formData.eta_to}
              onChange={handleChange}
              className="input-field"
            />
          </div>
        </div>

        {/* Helpers Needed */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-navy-700 mb-2">
            <Users className="h-4 w-4 inline mr-1" />
            Helpers Needed
          </label>
          <div className="flex items-center space-x-3">
            {[1, 2, 3, 4].map((num) => (
              <button
                key={num}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, helpers_needed: num }))}
                className={`w-12 h-12 rounded-lg font-medium transition-colors ${
                  formData.helpers_needed === num
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-navy-700 hover:bg-gray-200'
                }`}
              >
                {num}
              </button>
            ))}
          </div>
        </div>

        {/* Berth Number */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-navy-700 mb-2">
            Berth Number (optional)
          </label>
          <input
            type="text"
            name="berth_number"
            value={formData.berth_number}
            onChange={handleChange}
            className="input-field"
            placeholder="e.g., A-42"
          />
        </div>

        {/* Notes */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-navy-700 mb-2">
            Special Notes (optional)
          </label>
          <textarea
            name="special_notes"
            value={formData.special_notes}
            onChange={handleChange}
            rows={4}
            className="input-field"
            placeholder="Any special instructions or requirements..."
          />
        </div>

        {/* Submit Button */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="btn-primary disabled:opacity-50"
          >
            {saving ? (
              <span className="flex items-center">
                <span className="spinner h-5 w-5 mr-2" />
                Saving...
              </span>
            ) : (
              <>
                <Save className="h-5 w-5 mr-2" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default EditRequest;
